/**
 * Zentrale Status-Helper für die Anzeige von Anfragen-Status.
 * 
 * Status-Semantik:
 * - pending: Anfrage wartet auf Antworten
 * - accepted: Anfrage wurde akzeptiert (alle oder teilweise)
 * - declined: Anfrage wurde abgelehnt
 * - filled: Alle Lektionen sind besetzt
 * - cancelled: Anfrage wurde storniert
 * - expired: Anfrage ist abgelaufen
 * - partially_filled: Mindestens eine Lektion ist besetzt
 * - partially_accepted: Mindestens ein Block wurde akzeptiert
 * 
 * Display-Logik:
 * - Für getDisplayStatus: Finale Status (cancelled, expired, filled, declined) werden immer angezeigt
 * - Für getIncomingDisplayStatus: Finale Status (cancelled, expired, filled) werden immer angezeigt
 * - Bei accepted: nur upgrade zu partially_accepted möglich, NIEMALS downgrade zu pending
 * - Bei pending: composite_status anzeigen, falls vorhanden (z.B. partially_accepted)
 */

export const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800',
  no_substitute_found: 'bg-orange-100 text-orange-800',
  contacted: 'bg-orange-100 text-orange-700',
  timed_out: 'bg-orange-50 text-orange-600',
  cancelled: 'bg-gray-100 text-gray-800',
  partially_filled: 'bg-orange-100 text-orange-800',
  partially_accepted: 'bg-yellow-100 text-yellow-800',
  filled: 'bg-green-100 text-green-800',
  expired: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-amber-100 text-amber-800',
  accepted_not_selected: 'bg-gray-100 text-gray-600',
  waiting: 'bg-blue-100 text-blue-800',
  teacher_overlap: 'bg-red-100 text-red-800',
  room_double_booked: 'bg-orange-100 text-orange-800',
  class_double_booked: 'bg-orange-100 text-orange-800',
  substitution_double_booking: 'bg-red-100 text-red-800',
  overtime: 'bg-orange-100 text-orange-800',
  scheduled: 'bg-blue-100 text-blue-800',
  executed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  notified: 'bg-green-100 text-green-800',
  resent: 'bg-blue-100 text-blue-800',
  // prionew-v2: Wizard-Recontact (zweite Anfragerunde für offene Lektionen)
  recontacted: 'bg-orange-100 text-orange-700',
  recontacted_declined: 'bg-red-100 text-red-800',
  recontacted_timed_out: 'bg-orange-50 text-orange-600',
  // R5: Director-Gate-Status sehen in Lehrer-Ansichten (Karten, Liste, Timeline)
  director_pending: 'bg-indigo-100 text-indigo-800',
  director_returned: 'bg-orange-100 text-orange-800',
  distribution_running: 'bg-amber-100 text-amber-800',
}

export const STATUS_LABELS: Record<string, string> = {
  pending: 'Offen',
  accepted: 'Akzeptiert',
  declined: 'Abgelehnt',
  no_substitute_found: 'Kein Vertreter gefunden',
  contacted: 'Kontaktiert',
  timed_out: 'Noch offen',
  cancelled: 'Storniert',
  partially_filled: 'Teilweise besetzt',
  partially_accepted: 'Teilweise akzeptiert',
  filled: 'Besetzt',
  expired: 'Abgelaufen',
  in_progress: 'In Bearbeitung',
  accepted_not_selected: 'Nicht ausgewählt',
  waiting: 'Teilannahme',
  teacher_overlap: 'Doppelbuchung',
  room_double_booked: 'Raum-Kollision',
  class_double_booked: 'Klassen-Kollision',
  substitution_double_booking: 'Vertretungs-Kollision',
  overtime: 'Überstunden',
  scheduled: 'Geplant',
  executed: 'Ausgeführt',
  failed: 'Fehlgeschlagen',
  notified: 'Bestätigt',
  resent: 'Erneut gesendet',
  // prionew-v2: Wizard-Recontact
  recontacted: 'Erneut angefragt',
  recontacted_declined: 'Abgelehnt',
  recontacted_timed_out: 'Keine Antwort',
  // R5: Director-Gate-Status (gleiche Labels wie Admin-Sicht, SubstitutionManagementPage)
  director_pending: 'Direktor-Freigabe ausstehend',
  director_returned: 'Zurückgestellt (angepasst)',
  distribution_running: 'Verteilung läuft',
}

/**
 * Verbindliche Badge-Farb-Tabelle für runde Teacher-Badges (Cascade).
 * Einzige Quelle für die Badge-Farbe — inkl. Dark-Mode.
 * - contacted:  dunkles Orange (aktuell kontaktiert, live)
 * - timed_out:  Pastell-Orange, opacity-70 (Timeout, kann noch annehmen)
 * - waiting:    blau (Teilannahme / Wartepool)
 * - declined:   rot
 * - accepted:   grün
 * - accepted_not_selected: grau (verloren)
 */
export const BADGE_COLORS: Record<string, string> = {
  accepted: 'bg-green-100 border-green-500 text-green-800 dark:bg-green-900/30 dark:border-green-400 dark:text-green-400',
  assigned: 'bg-green-100 border-green-500 text-green-800 dark:bg-green-900/30 dark:border-green-400 dark:text-green-400',
  contacted: 'bg-orange-100 border-orange-500 text-orange-800 dark:bg-orange-900/30 dark:border-orange-400 dark:text-orange-400',
  timed_out: 'bg-orange-50 border-orange-300 text-orange-500 opacity-70 dark:bg-orange-900/20 dark:border-orange-700 dark:opacity-80 dark:text-orange-400',
  waiting: 'bg-blue-100 border-blue-500 text-blue-800 dark:bg-blue-900/30 dark:border-blue-400 dark:text-blue-400',
  declined: 'bg-red-100 border-red-500 text-red-800 dark:bg-red-900/30 dark:border-red-400 dark:text-red-400',
  expired: 'bg-red-100 border-red-500 text-red-800 dark:bg-red-900/30 dark:border-red-400 dark:text-red-400',
  accepted_not_selected: 'bg-gray-100 border-gray-400 text-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400',
  pending: 'bg-gray-100 border-gray-300 text-gray-600 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400',
  cancelled: 'bg-gray-100 border-gray-400 text-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400',
  // prionew-v2: Wizard-Recontact — visuell wie ihre Erstkontakt-Pendants
  // (recontacted ≙ contacted/aktiv, terminale Antworten ≙ declined/timed_out).
  recontacted: 'bg-orange-100 border-orange-500 text-orange-800 dark:bg-orange-900/30 dark:border-orange-400 dark:text-orange-400',
  recontacted_declined: 'bg-red-100 border-red-500 text-red-800 dark:bg-red-900/30 dark:border-red-400 dark:text-red-400',
  recontacted_timed_out: 'bg-orange-50 border-orange-300 text-orange-500 opacity-70 dark:bg-orange-900/20 dark:border-orange-700 dark:opacity-80 dark:text-orange-400',
}

export const BADGE_LABELS: Record<string, string> = {
  accepted: 'Angenommen',
  assigned: 'Angenommen',
  contacted: 'Kontaktiert',
  timed_out: 'Noch offen',
  waiting: 'Teilannahme',
  declined: 'Abgelehnt',
  expired: 'Abgelaufen',
  accepted_not_selected: 'Nicht ausgewählt',
  pending: 'Offen',
  cancelled: 'Storniert',
  // prionew-v2: Wizard-Recontact
  recontacted: 'Erneut angefragt',
  recontacted_declined: 'Abgelehnt',
  recontacted_timed_out: 'Keine Antwort',
}

/** Einzige Quelle für die Farbe eines runden Teacher-Badges. */
export function getBadgeColor(status: string): string {
  return BADGE_COLORS[status] ?? BADGE_COLORS.pending ?? ''
}

/** Einzige Quelle für das Label eines runden Teacher-Badges. */
export function getBadgeLabel(status: string): string {
  return BADGE_LABELS[status] || status
}

const _LIVE_CASCADE_BADGE_STATUSES = [
  'accepted',
  'assigned',
  'contacted',
  'timed_out',
  'waiting',
  'pending',
  'recontacted',
  'recontacted_timed_out',
]

/**
 * Badge-Status in der Kaskade: bei stornierter Anfrage keine Live-Farben
 * (grün/orange/blau), auch wenn residuale Entry-Status noch accepted/timed_out sind.
 */
export function getCascadeTeacherBadgeStatus(
  contactStatus: string,
  requestStatus?: string | null,
): string {
  if (requestStatus === 'cancelled' && _LIVE_CASCADE_BADGE_STATUSES.includes(contactStatus)) {
    return 'cancelled'
  }
  return contactStatus
}

const FINAL_STATUSES = ['cancelled', 'expired', 'filled', 'declined', 'no_substitute_found']
const _INCOMING_FINAL_STATUSES = ['cancelled', 'expired', 'filled']

/**
 * Finale Request-Status, die jeden Kaskaden-Aggregat-Status (cascade_status)
 * dominieren müssen — sonst würde z.B. eine stornierte Karte mit residualem
 * cascade_status='partially_accepted' fälschlich als aktiv angezeigt.
 */
export function isFinalRequestStatus(status: string | null | undefined): boolean {
  return !!status && FINAL_STATUSES.includes(status)
}

export function getDisplayStatus(status: string, compositeStatus: string | null | undefined): string {
  if (FINAL_STATUSES.includes(status)) {
    return status
  }
  if (status === 'accepted') {
    if (compositeStatus === 'partially_accepted') return compositeStatus
    return status
  }
  if (status === 'pending') {
    return compositeStatus || status
  }
  return status
}

export function getIncomingDisplayStatus(request: { status: string; cascade_status?: string | null }): string {
  if (_INCOMING_FINAL_STATUSES.includes(request.status)) {
    return request.status
  }
  if (request.cascade_status != null) {
    return request.cascade_status
  }
  return request.status
}

/**
 * Status fuer das BADGE auf der "Anfragen an mich"-Karte.
 *
 * Zeigt den EIGENEN Status der betrachtenden Lehrperson an, sobald sie
 * geantwortet hat (waiting/accepted/declined/accepted_not_selected/timed_out),
 * damit z.B. eine Teilannahme ("Teilannahme"/waiting) sichtbar wird — statt
 * des Aggregats "In Bearbeitung". Solange die Lehrperson noch nicht geantwortet
 * hat (contacted/pending), wird das Kaskaden-Aggregat
 * (getIncomingDisplayStatus) gezeigt.
 *
 * Wird NUR fuer das Badge verwendet; Tab-Filter und Zaehler nutzen weiterhin
 * getIncomingDisplayStatus, damit teil-angenommene Anfragen in der "Offen"-Liste
 * bleiben.
 */
const _VIEWER_DECISIVE_STATUSES = [
  'waiting',
  'accepted',
  'declined',
  'accepted_not_selected',
  'timed_out',
  // prionew-v2: Recontact aktiv → Lehrer sieht seinen eigenen Status
  'recontacted',
]

export function getIncomingBadgeStatus(request: {
  status: string
  cascade_status?: string | null
  viewer_step_status?: string | null
}): string {
  // Finale Request-Status dominieren (cancelled/expired/filled).
  if (_INCOMING_FINAL_STATUSES.includes(request.status)) {
    return request.status
  }
  const own = request.viewer_step_status
  if (own && _VIEWER_DECISIVE_STATUSES.includes(own)) {
    return own
  }
  return getIncomingDisplayStatus(request)
}

export function getStatusColor(status: string): string {
  return STATUS_COLORS[status] || 'bg-gray-100 text-gray-800'
}

export function getStatusLabel(status: string): string {
  return STATUS_LABELS[status] || status
}

export function getAcceptedStatusLabel(
  request: {
    status: string
    cascade_status?: string | null
    viewer_step_status?: string | null
    substitute_teacher_id?: string | null
    accepted_teacher_code?: string | null
  },
  currentTeacherId: string | undefined,
): string {
  const displayStatus = getIncomingBadgeStatus(request)
  const baseLabel = getStatusLabel(displayStatus)

  if (displayStatus !== 'accepted') return baseLabel

  const subId = request.substitute_teacher_id
  const code = request.accepted_teacher_code

  if (!subId || !code) return baseLabel

  const isSelf = currentTeacherId != null && subId === currentTeacherId
  if (isSelf) return baseLabel

  return `Akzeptiert von ${code}`
}

const BORDER_COLORS: Record<string, string> = {
  pending: 'border-l-yellow-500',
  partially_accepted: 'border-l-yellow-500',
  partially_filled: 'border-l-orange-500',
  accepted: 'border-l-green-500',
  filled: 'border-l-green-500',
  declined: 'border-l-red-500',
  no_substitute_found: 'border-l-orange-500',
  timed_out: 'border-l-amber-500',
  cancelled: 'border-l-gray-400',
  expired: 'border-l-gray-400',
  in_progress: 'border-l-amber-500',
  accepted_not_selected: 'border-l-gray-400',
  // R5: Director-Gate-Rahmen
  director_pending: 'border-l-indigo-500',
  director_returned: 'border-l-orange-500',
  distribution_running: 'border-l-amber-500',
}

export function getBorderLColor(status: string): string {
  return BORDER_COLORS[status] || 'border-l-gray-400'
}
